import type { Placement } from '@floating-ui/react';
import { arrow, offset, flip, shift } from '@floating-ui/react';
import type { CSSProperties, FC, ReactNode, RefObject } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';
import { createPortal } from 'react-dom';
import { Divider } from 'src/components/dividers';
import { useDropdown } from 'src/hooks/use-dropdown';
import { useHeaderHeight } from 'src/hooks/use-header-height';
import { useMounted } from 'src/hooks/use-mounted';
import { flyoutClasses } from 'src/styles';
import { cn } from 'src/utils/cn';

const borderRadius = 5;

export const tooltipContainerClasses = cn(
  flyoutClasses,
  `
    pointer-events-none z-1 [overflow:initial] px-xl py-lg
    [word-break:break-word]
    legacy:px-[1.4rem] legacy:py-[0.8rem]
  `
);

const arrowOffset = '-4px';

type Parent = {
  portalId: string;
  open: boolean;
};

const TooltipContext = createContext<Parent | null>(null);

type TargetProps = { 'aria-labelledby'?: string };

type Props = {
  initialOpen?: boolean;
  /**
   * The content of the tooltip. If this is value is falsy, the children will be rendered directly
   * without a tooltip.
   */
  content: ReactNode;
  children: (props: TargetProps) => ReactNode;
  /**
   * If you pass true _or_ false, you'll signal that the tooltip
   * does NOT control its active state on its own, but from the outside.
   * You likely want to use this together with the `useTooltipControl` hook.
   */
  controlOpen?: boolean;
  dataTestId?: string;
  /**
   * If you pass an `positionRef`, the tooltip will be positioned relative to the `positionRef`.
   * This is completely optional. By default it will be position relative to its children.
   */
  positionRef?: RefObject<HTMLElement>;
  /**
   * Be *very* cautious with this prop. If you set it to `true`, the tooltip will not be focusable.
   * That would make it impossible to see the tooltip with the keyboard. *Only* use this if the
   * `children` can be reached with the keyboard and take care of showing the tooltip.
   */
  noTabIndex?: boolean;
  /**
   * Sometimes we might use <DataList> inside a <Tooltip>.
   * In this case, we might want to set the maxWidth to fit the content and avoid line breaks.
   */
  maxWidth?: number;
};

export const Tooltip: FC<Props> = (props) => {
  const parent = useContext(TooltipContext);

  if (!props.content) return <>{props.children({})}</>;

  if (parent) {
    return <TooltipChild {...props} parent={parent} />;
  } else {
    return <TooltipParent {...props} />;
  }
};

type ChildProps = {
  content: ReactNode;
  children: (props: TargetProps) => ReactNode;
  parent: Parent;
};

const getArrowPlacement = (originalPlacement: Placement): string => {
  return {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[originalPlacement.split('-')[0]]!;
};

const TooltipChild: FC<ChildProps> = ({ children, content, parent }) => {
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);

  useEffect(() => {
    if (parent.open) {
      setPortalContainer(document.getElementById(parent.portalId));
    } else {
      setPortalContainer(null);
    }
  }, [parent.open, parent.portalId]);

  return (
    <>
      {portalContainer &&
        createPortal(
          <>
            <Divider
              direction="horizontal"
              className={cn(
                `
                  -mx-xl w-[calc(100%+var(--spacing-xl)*2)]
                  not-legacy:my-lg
                  legacy:-mx-[1.4rem] legacy:w-[calc(100%+1.4rem*2)]
                `,
                `legacy:my-[0.8rem]`
              )}
            />
            {content}
          </>,
          portalContainer
        )}
      {children({})}
    </>
  );
};

const TooltipParent: FC<Props> = ({
  children,
  content,
  initialOpen,
  controlOpen,
  dataTestId,
  positionRef,
  noTabIndex,
  maxWidth,
}) => {
  const id = useId();
  const portalId = `portal-${id}`;
  const mounted = useMounted();
  const { height: headerHeight } = useHeaderHeight();

  const arrowElement = useRef<HTMLDivElement>(null);

  const dropdown = useDropdown({
    initialOpen,
    placement: 'top',
    middleware: [
      shift({ padding: 10 }),
      flip({
        padding: {
          top: 10 + headerHeight,
          bottom: 10,
          left: 10,
          right: 10,
        },
      }),
      offset({ mainAxis: 10 }),
      arrow({ element: arrowElement, padding: borderRadius }),
    ],
  });

  // onMouseLeave does not work, if your child is disabled
  // see https://github.com/chakra-ui/chakra-ui/pull/2272/files#diff-fbb684f6a8aac198338c32fb54583cdee24725847675fd1d9396f4767038c7bcR154
  // for the inspiration of this workaround
  useEffect(() => {
    const domRef = dropdown.refs.domReference.current;
    if (!domRef) return;

    const onMouseLeave = () => {
      dropdown.setOpen(false);
    };
    domRef.addEventListener('mouseleave', onMouseLeave);
    return () => {
      domRef.removeEventListener('mouseleave', onMouseLeave);
    };
    // oxlint-disable-next-line exhaustive-deps
  }, [dropdown.refs.domReference, dropdown.setOpen]);

  // if we scroll _anywhere_ we'll close the tooltip (_anywhere_ -> capture set to true)
  // it looks weird if the target of a tooltip becomes invisible (e.g. it's inside a container with
  // "overflow: hidden" or it gets covered by a "position: sticky" element)
  useEffect(() => {
    const onScroll = () => {
      if (mounted.current) dropdown.setOpen(false);
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll);
    // oxlint-disable-next-line exhaustive-deps
  }, [dropdown.setOpen, mounted]);

  const parent = useMemo(
    () => ({ open: dropdown.open, portalId }),
    [dropdown.open, portalId]
  );

  useEffect(() => {
    if (controlOpen === undefined) return;
    dropdown.setOpen(controlOpen);
    // oxlint-disable-next-line exhaustive-deps
  }, [dropdown.setOpen, controlOpen]);

  const activeHandlers =
    controlOpen === undefined
      ? {
          onFocus: () => dropdown.setOpen(true),
          onBlur: () => dropdown.setOpen(false),
          onMouseEnter: () => dropdown.setOpen(true),
          // surprise! this does not work, if the child is disabled
          // see the workaround using useEffect
          // onMouseLeave={() => setActive(false)}
          onClick: () => dropdown.setOpen(!dropdown.open),
        }
      : {};

  useLayoutEffect(() => {
    if (!positionRef) return;
    dropdown.refs.setPositionReference(positionRef.current);
    // oxlint-disable-next-line exhaustive-deps
  }, [positionRef?.current]);
  return (
    <TooltipContext.Provider value={parent}>
      <span {...activeHandlers}>
        <span
          ref={dropdown.refs.setReference}
          {...dropdown.getReferenceProps()}
          tabIndex={noTabIndex ? undefined : 0}
          data-testid={
            dataTestId ? `${dataTestId}-tooltip-trigger` : 'tooltip-trigger'
          }
          className={`
            focus-within:spaced-focus-style
            not-legacy:[&>.ui-icon,&>[data-icon]]:text-foreground-primary-inverted
          `}
        >
          {children({
            'aria-labelledby': dropdown.open ? id : undefined,
          })}
        </span>

        {(controlOpen ?? dropdown.open) &&
          // render tooltip in a portal in case it is used inside a "overflow: hidden" container,
          // so it gets not clipped
          createPortal(
            <div
              id={id}
              role="tooltip"
              ref={dropdown.refs.setFloating}
              {...dropdown.getFloatingProps()}
              style={
                {
                  '--tooltip-max-width': `${maxWidth ?? 24}rem`,
                  ...dropdown.style,
                } as CSSProperties
              }
              className={cn(
                tooltipContainerClasses,
                `max-w-(--tooltip-max-width)`
              )}
              data-testid={
                dataTestId ? `${dataTestId}-tooltip-content` : 'tooltip-content'
              }
            >
              {content}
              <div id={portalId} />
              <div
                ref={arrowElement}
                className={`
                  absolute size-md rotate-45 bg-background-surface-default
                  legacy:bg-white
                `}
                // oxlint-disable-next-line prisma/tailwind-no-inline-styles
                style={{
                  left: dropdown.middlewareData.arrow?.x ?? undefined,
                  top: dropdown.middlewareData.arrow?.y ?? undefined,
                  bottom: arrowOffset,
                  [getArrowPlacement(dropdown.placement)]: arrowOffset,
                }}
              />
            </div>,
            document.body
          )}
      </span>
    </TooltipContext.Provider>
  );
};

/**
 * Use this hook if a tooltip should not control itself,
 * but when it should be controlled by another element.
 *
 * ```
 * const myTooltipControl = useTooltipControl();
 * <button {...myTooltipControl.handlers}>Click</button>
 * <Tooltip controlOpen={statusTooltipControl.open}></Tooltip>
 * ```
 */
export function useTooltipControl({ initialActive = false } = {}) {
  const [open, setOpen] = useState(initialActive);

  const handlers = useMemo(
    () => ({
      onFocus: () => setOpen(true),
      onBlur: () => setOpen(false),
      onMouseEnter: () => setOpen(true),
      onMouseLeave: () => setOpen(false),
    }),
    []
  );

  return { open, handlers };
}
